import { DomUtils, parseDocument } from 'htmlparser2';

const OUTGROW_HOSTNAME = 'atlassian931.outgrow.us';
const OUTGROW_LOADER_HOSTNAME = 'dyv6f9ner1ir9.cloudfront.net';
const SUPPORT_FORM_HOSTNAMES = new Set([
	'support-form-app-ui.prod-east.frontend.public.atl-paas.net',
	'support-form-app-ui.stg-east.frontend.public.atl-paas.net',
]);
const SUPPORT_FORM_SHIM_PATH = '/assets/js-app-shim.js';
const SUPPORT_FORM_PRODUCTION_MANIFEST_URL =
	'https://support-form-app-ui.prod-east.frontend.public.atl-paas.net/assets/asset-manifest.json';
const SUPPORT_FORM_STAGING_MANIFEST_URL =
	'https://support-form-app-ui.stg-east.frontend.public.atl-paas.net/assets/asset-manifest.json';
const SAFE_SUPPORT_FORM_ASSET_KEY = /^[a-z0-9][a-z0-9-]{0,127}\.(?:js|css)$/;
const ECOSYSTEMS_IFRAME_HOSTNAME = 'atlassian.wvc-test.ecosystems.io';
const ECOSYSTEMS_IFRAME_ID = 'webapp-frame';
const SAFE_EMBED_ID = /^[A-Za-z0-9_-]{1,128}$/;

type ParsedNode = ReturnType<typeof parseDocument>['children'][number];
type ParsedElement = Extract<ParsedNode, { attribs: Record<string, string> }>;

export type ParsedThirdPartyEmbed =
	| {
			provider: 'cstStaticForm';
			formName: string;
			height: number;
	  }
	| {
			provider: 'outgrow';
			embedId: string;
			embedUrl: string;
			title: string;
	  }
	| {
			provider: 'supportForm';
			scriptKey: string;
			stylesheetKey: string;
	  }
	| {
			provider: 'ecosystemsIframe';
			embedUrl: string;
	  };

const isWhitespace = (node: ParsedNode): boolean => node.type === 'text' && node.data.trim() === '';

const meaningfulChildren = (children: ParsedNode[]): ParsedNode[] =>
	children.filter((node) => !isWhitespace(node));

const isElement = (node: ParsedNode | undefined, name: string): node is ParsedElement =>
	node !== undefined && DomUtils.isTag(node) && node.name === name;

const hasOnlyAttributes = (element: ParsedElement, allowedAttributes: string[]): boolean => {
	const allowed = new Set(allowedAttributes);
	return Object.keys(element.attribs).every(
		(attribute) => allowed.has(attribute) && !attribute.toLowerCase().startsWith('on'),
	);
};

const hasExactClasses = (element: ParsedElement, expectedClasses: string[]): boolean => {
	const actualClasses = (element.attribs['class'] ?? '').split(/\s+/).filter(Boolean).sort();
	return actualClasses.join(' ') === [...expectedClasses].sort().join(' ');
};

const getScriptText = (script: ParsedElement): string =>
	meaningfulChildren(script.children)
		.map((node) => (node.type === 'text' ? node.data : ''))
		.join('');

const isOptionalJavaScriptType = (element: ParsedElement): boolean => {
	const type = element.attribs['type'];
	return type === undefined || type === 'text/javascript';
};

const parseApprovedHttpsUrl = (value: string): URL | null => {
	try {
		const url = new URL(value);
		if (
			url.protocol !== 'https:' ||
			url.username !== '' ||
			url.password !== '' ||
			url.port !== ''
		) {
			return null;
		}
		return url;
	} catch {
		return null;
	}
};

const isApprovedOutgrowMount = (element: ParsedElement): boolean =>
	hasOnlyAttributes(element, ['class', 'id', 'data-title', 'data-url', 'data-width']) &&
	hasExactClasses(element, ['op-interactive']) &&
	meaningfulChildren(element.children).length === 0 &&
	element.attribs['data-width'] === '100%';

const isApprovedScriptElement = (element: ParsedElement, allowedAttributes: string[]): boolean =>
	hasOnlyAttributes(element, allowedAttributes) && isOptionalJavaScriptType(element);

const isApprovedOutgrowConfiguration = (
	embedId: string,
	title: string,
	approvedUrl: URL | null,
	scriptText: string,
): approvedUrl is URL =>
	SAFE_EMBED_ID.test(embedId) &&
	title !== '' &&
	approvedUrl?.hostname === OUTGROW_HOSTNAME &&
	approvedUrl.pathname === `/${embedId}` &&
	scriptText.includes(OUTGROW_LOADER_HOSTNAME) &&
	scriptText.includes(`initIframe('${embedId}')`);

const isApprovedSupportFormMount = (element: ParsedElement): boolean =>
	hasOnlyAttributes(element, ['class']) &&
	hasExactClasses(element, ['wac-form']) &&
	meaningfulChildren(element.children).length === 0;

const isApprovedSupportFormShimUrl = (url: URL | null): url is URL =>
	url !== null &&
	SUPPORT_FORM_HOSTNAMES.has(url.hostname) &&
	url.pathname === SUPPORT_FORM_SHIM_PATH &&
	url.search === '' &&
	url.hash === '';

const escapeRegularExpression = (value: string): string =>
	value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const hasStringProperty = (script: string, property: string, value: string): boolean =>
	new RegExp(`\\b${property}\\s*:\\s*(['"])${escapeRegularExpression(value)}\\1`).test(script);

const getSingleItemArrayProperty = (script: string, property: string): string | null => {
	const expression = new RegExp(`\\b${property}\\s*:\\s*\\[\\s*(['"])([^'"]+)\\1\\s*\\]`, 'g');
	let value: string | null = null;
	let match = expression.exec(script);
	while (match) {
		if (value !== null) return null;
		value = match[2] as string;
		match = expression.exec(script);
	}
	return value;
};

const getApprovedSupportFormConfiguration = (
	script: string,
): { scriptKey: string; stylesheetKey: string } | null => {
	if (
		!script.includes('window.jsAppShim') ||
		!script.includes(SUPPORT_FORM_PRODUCTION_MANIFEST_URL) ||
		!script.includes(SUPPORT_FORM_STAGING_MANIFEST_URL) ||
		!hasStringProperty(script, 'target', '.wac-form') ||
		!hasStringProperty(script, 'position', 'after') ||
		!hasStringProperty(script, 'appTargetId', 'support-form-app')
	) {
		return null;
	}

	const scriptKey = getSingleItemArrayProperty(script, 'scripts');
	const stylesheetKey = getSingleItemArrayProperty(script, 'styles');
	if (
		!scriptKey ||
		!stylesheetKey ||
		!SAFE_SUPPORT_FORM_ASSET_KEY.test(scriptKey) ||
		!SAFE_SUPPORT_FORM_ASSET_KEY.test(stylesheetKey) ||
		!scriptKey.endsWith('.js') ||
		!stylesheetKey.endsWith('.css') ||
		scriptKey.slice(0, -3) !== stylesheetKey.slice(0, -4)
	) {
		return null;
	}

	return { scriptKey, stylesheetKey };
};

const hasApprovedIframeStyles = (style: string): boolean => {
	const declarations = style
		.split(';')
		.map((declaration) => declaration.trim())
		.filter(Boolean);
	if (declarations.length !== 3) return false;

	const normalizedDeclarations = declarations.map((declaration) =>
		declaration.replace(/\s+/g, '').toLowerCase(),
	);
	return (
		new Set(normalizedDeclarations).size === 3 &&
		normalizedDeclarations.includes('width:100%') &&
		normalizedDeclarations.includes('border:none') &&
		normalizedDeclarations.includes('overflow:hidden')
	);
};

const APPROVED_ECOSYSTEMS_RESIZE_SCRIPT =
	/^\s*window\.addEventListener\s*\(\s*(['"])message\1\s*,\s*\(?\s*event\s*\)?\s*=>\s*\{\s*if\s*\(\s*event\.data\.type\s*===\s*(['"])resize-iframe\2\s*\)\s*\{\s*const\s+iframe\s*=\s*document\.getElementById\s*\(\s*(['"])webapp-frame\3\s*\)\s*;\s*iframe\.style\.height\s*=\s*event\.data\.height\s*\+\s*(['"])px\4\s*;\s*\}\s*\}\s*\)\s*;?\s*$/;

const hasApprovedEcosystemsResizeSignature = (script: string): boolean =>
	APPROVED_ECOSYSTEMS_RESIZE_SCRIPT.test(script);

const isApprovedEcosystemsIframeNode = (iframe: ParsedElement): boolean =>
	hasOnlyAttributes(iframe, ['id', 'src', 'style', 'scrolling']) &&
	iframe.attribs['id'] === ECOSYSTEMS_IFRAME_ID &&
	iframe.attribs['scrolling'] === 'no' &&
	hasApprovedIframeStyles(iframe.attribs['style'] ?? '') &&
	meaningfulChildren(iframe.children).length === 0;

const isApprovedEcosystemsConfiguration = (embedUrl: URL | null, script: string): embedUrl is URL =>
	embedUrl?.hostname === ECOSYSTEMS_IFRAME_HOSTNAME &&
	embedUrl.pathname === '/' &&
	embedUrl.search === '' &&
	embedUrl.hash === '' &&
	hasApprovedEcosystemsResizeSignature(script);

const parseOutgrow = (root: ParsedElement): ParsedThirdPartyEmbed | null => {
	if (!hasOnlyAttributes(root, [])) return null;

	const children = meaningfulChildren(root.children);
	if (children.length !== 2) return null;

	const [mountNode, scriptNode] = children;
	if (!isElement(mountNode, 'div') || !isElement(scriptNode, 'script')) return null;
	if (!isApprovedOutgrowMount(mountNode)) return null;
	if (!isApprovedScriptElement(scriptNode, ['type'])) return null;

	const embedId = mountNode.attribs['id'] ?? '';
	const embedUrl = mountNode.attribs['data-url'] ?? '';
	const title = mountNode.attribs['data-title']?.trim() ?? '';
	const approvedUrl = parseApprovedHttpsUrl(embedUrl);
	const scriptText = getScriptText(scriptNode);

	if (!isApprovedOutgrowConfiguration(embedId, title, approvedUrl, scriptText)) return null;

	return { provider: 'outgrow', embedId, embedUrl: approvedUrl.toString(), title };
};

const parseSupportForm = (children: ParsedNode[]): ParsedThirdPartyEmbed | null => {
	if (children.length !== 3) return null;

	const [mountNode, externalScriptNode, inlineScriptNode] = children;
	if (
		!isElement(mountNode, 'div') ||
		!isElement(externalScriptNode, 'script') ||
		!isElement(inlineScriptNode, 'script')
	) {
		return null;
	}
	if (!isApprovedSupportFormMount(mountNode)) return null;
	if (
		!isApprovedScriptElement(externalScriptNode, ['src', 'type']) ||
		meaningfulChildren(externalScriptNode.children).length !== 0 ||
		!isApprovedScriptElement(inlineScriptNode, ['type'])
	) {
		return null;
	}

	const shimUrl = parseApprovedHttpsUrl(externalScriptNode.attribs['src'] ?? '');
	if (!isApprovedSupportFormShimUrl(shimUrl)) return null;

	const inlineScript = getScriptText(inlineScriptNode);
	const configuration = getApprovedSupportFormConfiguration(inlineScript);
	if (!configuration) return null;

	return { provider: 'supportForm', ...configuration };
};

const parseEcosystemsIframe = (children: ParsedNode[]): ParsedThirdPartyEmbed | null => {
	if (children.length !== 2) return null;

	const [iframeNode, scriptNode] = children;
	if (!isElement(iframeNode, 'iframe') || !isElement(scriptNode, 'script')) return null;
	if (
		!isApprovedEcosystemsIframeNode(iframeNode) ||
		!isApprovedScriptElement(scriptNode, ['type'])
	) {
		return null;
	}

	const embedUrl = parseApprovedHttpsUrl(iframeNode.attribs['src'] ?? '');
	if (!isApprovedEcosystemsConfiguration(embedUrl, getScriptText(scriptNode))) return null;

	return { provider: 'ecosystemsIframe', embedUrl: embedUrl.toString() };
};

// Recognize configuration only; never execute the pasted JavaScript.
const CST_STATIC_FORM_INITIALIZER =
	/^\s*(?:var|let|const)\s+([A-Za-z_$][\w$]*)\s*=\s*new\s+CstStaticForm\s*\(\s*\)\s*;\s*\1\.initialize\s*\(\s*\{([^{}]+)\}\s*\)\s*;\s*\1\.display\s*\(\s*\)\s*;?\s*$/;

const parseCstStaticFormConfiguration = (script: string): ParsedThirdPartyEmbed | null => {
	const configuration = CST_STATIC_FORM_INITIALIZER.exec(script)?.[2];
	if (!configuration) return null;
	const properties = configuration.trim().replace(/,\s*$/, '').split(',');
	if (properties.length !== 2) return null;
	const values = new Map<string, string>();
	for (const property of properties) {
		const match =
			/^\s*(?:(['"])(formName|height)\1|(formName|height))\s*:\s*(['"])([a-z0-9-]+)\4\s*$/.exec(
				property,
			);
		if (!match) return null;
		values.set(match[2] ?? match[3] ?? '', match[5] ?? '');
	}
	const formName = values.get('formName') ?? '';
	const height = values.get('height') ?? '';
	if (
		!/^[a-z0-9][a-z0-9-]{0,127}$/.test(formName) ||
		!/^[1-9]\d{0,5}$/.test(height) ||
		Number(height) > 100_000
	) {
		return null;
	}
	return { provider: 'cstStaticForm', formName, height: Number(height) };
};

const parseCstStaticForm = (root: ParsedElement): ParsedThirdPartyEmbed | null => {
	if (!hasOnlyAttributes(root, ['id']) || root.attribs['id'] !== 'cst-static-forms-container') {
		return null;
	}
	const children = meaningfulChildren(root.children);
	const [loader, initializer] = children;
	if (children.length !== 2 || !isElement(loader, 'script') || !isElement(initializer, 'script')) {
		return null;
	}
	if (
		!isApprovedScriptElement(loader, ['src', 'type']) ||
		meaningfulChildren(loader.children).length !== 0 ||
		!isApprovedScriptElement(initializer, ['type']) ||
		loader.attribs['src'] !==
			'https://support-forms-ui.atlassian.com/assets/js/cst-static-forms-api.js'
	) {
		return null;
	}
	return parseCstStaticFormConfiguration(getScriptText(initializer));
};

export const parseThirdPartyEmbedHtml = (rawHtml: string): ParsedThirdPartyEmbed | null => {
	if (rawHtml.trim() === '') return null;

	const document = parseDocument(rawHtml, {
		decodeEntities: true,
		lowerCaseAttributeNames: true,
		lowerCaseTags: true,
	});
	const roots = meaningfulChildren(document.children);
	if (roots.length === 1 && isElement(roots[0], 'div')) {
		const cstStaticForm = parseCstStaticForm(roots[0]);
		if (cstStaticForm) return cstStaticForm;
		const outgrow = parseOutgrow(roots[0]);
		if (outgrow) return outgrow;
	}

	return parseSupportForm(roots) ?? parseEcosystemsIframe(roots);
};
